import { Component, inject, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AccountType,
  BandwidthE911Dlr,
  BandwidthEmergencyCallingSettingsConfig,
  CallingType,
  DialPlanGroupEntry,
  DialPlanGroupEntryStatus,
  LicensingState,
  Microsoft360View,
  MicrosoftDialPlanFieldConfig,
  MicrosoftDialPlanGroupSearchResult,
  MicrosoftDialPlanInventory,
  PstnConnectivityType,
  SharedCallingExtension,
  SharedCallingRoutingPolicy,
  TeamsPhoneNumberAssignment,
  TeamsPhoneNumberAssignmentFieldConfig,
  TeamsVoicePolicies,
  TeamsVoicePoliciesFieldConfig,
  UsageType,
} from '../../../../../shared/models/generated/ziroModels';
import { EMPTY, forkJoin, Observable, of, Subscription, switchMap, timer } from 'rxjs';
import { ButtonStyles } from '../../../../../button/button.component';
import { ZiroIcons } from '../../../../../shared/models/ziro-icons.enum';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { MicrosoftDialPlansResource } from '../../../../shared/resources/ms-dial-plans.resource';
import { ActivatedRoute, Router } from '@angular/router';
import { BandwidthDynamicFormData } from './form/bandwidth-e911-dlrs-form/bandwidth-emergency-calling-form.component';
import { MicrosoftTeamsCallingFormComponent } from './form/microsoft-teams-calling-form.component';
import { cloneDeep, isEqual } from 'lodash';
import { catchError, delayWhen, filter, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { BandwidthEmergencyCallingFieldConfigResource } from '../../../../shared/resources/field-config/bandwidth-emergency-calling-field-config.resource';
import { BandwidthE911DlrsResource } from '../../../../shared/resources/bandwidth-e911-dlrs.resource';
import { MicrosoftDialPlanInventoriesResource } from '../../../../shared/resources/microsoft-dial-plan-inventories.resource';
import {
  ExtensionSelectorOption,
  ExtensionSelectorSize,
  ZiroExtensionSelectorConfig,
} from '../../../../shared/extension-selector/extension-selector.component';
import { TranslateService } from '@ngx-translate/core';
import { ZiroModalService } from '../../../../../shared/services/ziro-modal.service';
import { DraasSharedExtensionsResource } from '../../../../shared/resources/draas-shared-extensions.resource';
import { MicrosoftPhoneNumberAssignmentsFieldConfigResource } from '../../../../shared/resources/microsoft-phone-number-assignments-field-config.resource';
import { MicrosoftVoicePoliciesFieldConfigResource } from '../../../../shared/resources/microsoft-voice-policies-field-config.resource';
import { MicrosoftPhoneNumberAssignmentsResource } from '../../../../shared/resources/microsoft-phone-number-assignments.resource';
import { ToastTypes } from '../../../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../../../shared/services/toast.service';
import { Microsoft360ViewPollingService } from '../../../../../shared/services/microsoft/microsoft-360-view-polling.service';
import { UsageLocationContext } from '../../../../../shared/contexts/usage-location.context';
import { convertScreamingSnakeCaseToTitleCase } from '../../../../../shared/utils';

export interface DialPlanSelectorEntity {
  dialPlanGroupId: number;
  extension: string;
}

@Component({
  selector: 'ziro-microsoft-teams-calling-dial-plan-selector-form',
  templateUrl: './microsoft-teams-calling-dial-plan-selector-form.component.html',
  styleUrl: './microsoft-teams-calling-dial-plan-selector-form.component.scss',
  standalone: false,
  providers: [Microsoft360ViewPollingService, UsageLocationContext],
})
export class ZiroMicrosoftTeamsCallingDialPlanSelectorFormComponent
  extends ZiroFormAbstractDirective<DialPlanSelectorEntity>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private microsoftDialPlanInventoriesResource = inject(MicrosoftDialPlanInventoriesResource);
  private microsoftDialPlansResource = inject(MicrosoftDialPlansResource);
  private microsoftPhoneNumberAssignmentFieldConfigResource = inject(
    MicrosoftPhoneNumberAssignmentsFieldConfigResource,
  );
  private microsoftVoicePoliciesFieldConfigResource = inject(MicrosoftVoicePoliciesFieldConfigResource);
  private microsoftPhoneNumberAssignmentsResource = inject(MicrosoftPhoneNumberAssignmentsResource);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private bandwidthEmergencyCallingFieldConfigResource = inject(BandwidthEmergencyCallingFieldConfigResource);
  private bandwidthE911DlrsResource = inject(BandwidthE911DlrsResource);
  private _translateService = inject(TranslateService);
  private _ziroModalService = inject(ZiroModalService);
  private draasSharedExtensionsResource = inject(DraasSharedExtensionsResource);
  private _toastService = inject(ToastService);

  readonly microsoftDialPlanGroups = input<MicrosoftDialPlanFieldConfig[]>();
  readonly teamsLine = input<TeamsPhoneNumberAssignment>();
  readonly voicePolicies = input<TeamsVoicePolicies>();
  readonly upn = input<string>();
  readonly isZpcSyncWarningPresent = input<boolean>();
  readonly isResourceAccount = input<boolean>();
  readonly ms360View = input<Microsoft360View>();
  readonly sharedCallingPolicies = input<SharedCallingRoutingPolicy[]>();
  readonly tenantId = input<string>();

  @ViewChild(MicrosoftTeamsCallingFormComponent) msTeamsCallingFormComponent: MicrosoftTeamsCallingFormComponent;

  check: DialPlanGroupEntry;
  onPremUpn: string;
  initialZiroDraasSharedCallingExtension: SharedCallingExtension;
  isBandwidthConfiguredWithoutE911Dlr = false;
  phoneNumberAssignmentFieldConfig: TeamsPhoneNumberAssignmentFieldConfig;
  voicePoliciesFieldConfig: TeamsVoicePoliciesFieldConfig;
  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  CallingType = CallingType;

  showRecentlyDeleted = true;
  showUnavailable = false;
  isSearching: boolean;
  isDialPlanGroupNotFoundForExistingLine = false;
  isDialPlanAccessForbidden = true;
  hasExtensionError = false;
  isNumberLocationValidationInProgress = false;
  showValue = true;
  formConfig: ZiroFormConfig;
  isDisplayForm = false;
  isBandwidthRange = false;
  bandwidthEmergencyCallingFieldConfig: BandwidthEmergencyCallingSettingsConfig;
  bandwidthEntity: BandwidthDynamicFormData;
  initialBandwidthEndpointId: string;
  isExtensionChanged = false;
  isAdWriteDownConfigured = false;
  initialLineUri: string;
  initialLineUriExtension: string;

  lastDialPlanGroup: MicrosoftDialPlanFieldConfig;
  selectedDialPlanGroup: MicrosoftDialPlanFieldConfig;
  initialDialPlanGroup: MicrosoftDialPlanFieldConfig;
  dialPlanInventories: DialPlanGroupEntry[] = [];
  isSubmitted = false;
  licenseIncompatibleAlert: string = null;
  dialPlanGroupSharedCallingPolicy: SharedCallingRoutingPolicy;
  isDisplaySharedCallingAlert = false;
  isExistingSharedCalling = false;
  userUsageLocation = '';
  numberUsageLocation = '';
  initialExistingNumber: string;

  /** Resource account + extension DPG: user may save main number without an extension (#22934). */
  isMainNumberSelected = false;

  mainNumberInventoryEntry: DialPlanGroupEntry | null = null;

  private _lastSelectedExtension = '';
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.onPremUpn = this.ms360View().onPremiseUserPrincipalName;
    const teamsLine = this.teamsLine();
    this.initialLineUri = cloneDeep(teamsLine?.lineUri);
    this.initialLineUriExtension = cloneDeep(teamsLine?.lineUriExtension);
    this.isDisplayForm = true;
    const existingSharedCallingVoicePolicyNumber =
      this.ms360View()?.teamsVoicePolicies?.sharedCallRoutingPolicy?.resourceAccountLineUri;
    this.isExistingSharedCalling = !!existingSharedCallingVoicePolicyNumber;
    if (teamsLine !== null || existingSharedCallingVoicePolicyNumber) {
      this.showValue = false;
      // Prioritize assigned number over shared calling - get phone number assignment if teams line exists
      const phoneNumberAssignment$ = teamsLine?.lineUri
        ? this.microsoftPhoneNumberAssignmentsResource.get(this.upn(), this.tenantId())
        : of(null);
      phoneNumberAssignment$
        .pipe(
          delayWhen(() => this._waitUntilSelectedDialPlanIsSet()),
          filter(() => !!this.selectedDialPlanGroup),
          switchMap((phoneNumberAssignment) =>
            forkJoin([
              phoneNumberAssignment?.lineUri
                ? this.microsoftPhoneNumberAssignmentFieldConfigResource.get(
                    this.tenantId(),
                    phoneNumberAssignment.lineUri,
                  )
                : of(null),
              this.microsoftVoicePoliciesFieldConfigResource.get(
                this.tenantId(),
                this.selectedDialPlanGroup.id,
                this.isResourceAccount(),
              ),
            ]),
          ),
          tap(([phoneNumberAssignmentFieldConfig, voicePoliciesFieldConfig]) => {
            this.phoneNumberAssignmentFieldConfig = phoneNumberAssignmentFieldConfig;
            this.voicePoliciesFieldConfig = voicePoliciesFieldConfig;
          }),
          catchError((err) => {
            if (
              err.status === 422 &&
              err.error.reasonCode === 'INSUFFICIENT_PERMISSION_TO_VIEW_OR_UPDATE_DIAL_PLAN_GROUP'
            ) {
              this._handleNoPermissionForDialPlanGroup(err);
              this.isDialPlanAccessForbidden = true;
              return EMPTY;
            }
            throw err;
          }),
        )
        .subscribe(() => {
          this.showValue = true;
        });
    }

    this.ziroFormsUpdate$.subscribe((changes) => {
      if (changes.old?.extension !== changes.new?.extension) {
        if (changes.new.extension) {
          this.isMainNumberSelected = false;
          this._lastSelectedExtension = changes.new.extension;
          this._syncExtensionRequiredState();
          this.showValue = false;
          const isExtensionType = this._isDialPlanExtensionType();
          const formattedExtension = isExtensionType
            ? this.selectedDialPlanGroup.mainNumber
            : changes.new.extension.split(';')[0];
          forkJoin([
            this.microsoftPhoneNumberAssignmentFieldConfigResource.get(this.tenantId(), formattedExtension),
            this.microsoftVoicePoliciesFieldConfigResource.get(
              this.tenantId(),
              this.selectedDialPlanGroup.id,
              this.isResourceAccount(),
            ),
          ])
            .pipe(
              catchError((err) => {
                this.showValue = true;
                this._toastService.push(
                  ToastTypes.ERROR,
                  this.ziroIcons.EXCLAMATION,
                  'tkey;shared.toast.operation_failed.title',
                  err?.error?.description ??
                    'tkey;microsoft_360.view.teams.extensionSelection.main_number.load_failed.message',
                );
                return EMPTY;
              }),
            )
            .subscribe(([phoneNumberAssignmentFieldConfig, voicePoliciesFieldConfig]) => {
              const teamsLineValue = this.teamsLine();
              if (teamsLineValue && isExtensionType) {
                teamsLineValue.lineUriExtension = changes.new.extension;
              } else if (
                teamsLineValue?.lineUriExtension &&
                !phoneNumberAssignmentFieldConfig.lineUriExtension.defaultValue
              ) {
                // If we are switching to a dial plan group which does not have the shorthand extension set, clear it
                teamsLineValue.lineUriExtension = '';
              } else if (
                teamsLineValue &&
                !teamsLineValue.lineUriExtension &&
                phoneNumberAssignmentFieldConfig.lineUriExtension.defaultValue
              ) {
                // If we are switching from a dial plan group which does not have the shorthand extension set, set it
                teamsLineValue.lineUriExtension = phoneNumberAssignmentFieldConfig.lineUriExtension.defaultValue;
              }

              this.phoneNumberAssignmentFieldConfig = phoneNumberAssignmentFieldConfig;
              this.voicePoliciesFieldConfig = voicePoliciesFieldConfig;

              if (this.isBandwidthRange) {
                this.isExtensionChanged = true;
                this.bandwidthEmergencyCallingFieldConfigResource
                  .get(this.tenantId(), this.upn(), this.entity.extension)
                  .subscribe((bandwidthEmergencyCallingFieldConfig) => {
                    this.bandwidthEmergencyCallingFieldConfig = bandwidthEmergencyCallingFieldConfig;
                    if (!isEqual(this.lastDialPlanGroup, this.selectedDialPlanGroup)) {
                      this.bandwidthEntity = {
                        dynamicGeolocationCallerName:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationCallerName.value,
                        dynamicGeolocationPreferredLanguage:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationPreferredLanguage.defaultOption,
                        dynamicGeolocations: bandwidthEmergencyCallingFieldConfig.dynamicGeolocations.defaultOption,
                        dynamicGeolocationCallbackNumber:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationCallbackNumber.value,
                        dynamicGeolocationEndpointId:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationEndpointId.value,
                      };
                    } else {
                      this.bandwidthEntity = {
                        ...this.bandwidthEntity,
                        dynamicGeolocationCallbackNumber:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationCallbackNumber.value,
                        dynamicGeolocationEndpointId:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationEndpointId.value,
                      };
                    }
                    this.showValue = true;
                    if (!this.lastDialPlanGroup || this.lastDialPlanGroup.id !== this.selectedDialPlanGroup.id) {
                      this.lastDialPlanGroup = this.selectedDialPlanGroup;
                    }
                  });
              } else {
                this.showValue = true;
                this.bandwidthEmergencyCallingFieldConfig = null;
              }
            });
        }
      }
    });

    const microsoftDialPlanGroups = this.microsoftDialPlanGroups();
    if ((teamsLine || existingSharedCallingVoicePolicyNumber) && !!microsoftDialPlanGroups) {
      this.isSearching = true;
      const sharedCallingDialPlanGroup = microsoftDialPlanGroups.find(
        (group) => group.mainNumber === existingSharedCallingVoicePolicyNumber,
      );

      // Prioritize assigned number over shared calling - use assigned number dial plan if it exists
      const dialPlan$: Observable<MicrosoftDialPlanFieldConfig[] | MicrosoftDialPlanGroupSearchResult[]> =
        teamsLine?.lineUri
          ? this.microsoftDialPlansResource.getDialPlanInventoriesByLineUri(
              this.tenantId(),
              teamsLine?.lineUri,
              teamsLine?.lineUriExtension,
            )
          : existingSharedCallingVoicePolicyNumber
            ? of(sharedCallingDialPlanGroup ? [sharedCallingDialPlanGroup] : [])
            : of([]);
      dialPlan$.subscribe((dialPlan) => {
        const searchResults = this._normalizeDialPlanLoadResultToSearchResults(dialPlan);
        const effectiveDialPlan = this._resolveDialPlanSearchResultFromLineUri(searchResults, teamsLine);
        // check if bandwidth is enabled using the passed field config array from parent
        if (effectiveDialPlan.length) {
          const fieldConfig = this.microsoftDialPlanGroups().find(
            (msDialPlanFieldConfig: MicrosoftDialPlanFieldConfig) =>
              msDialPlanFieldConfig.id === effectiveDialPlan[0].id,
          );
          if (fieldConfig) {
            this.isBandwidthRange = fieldConfig.emergencyCallingSettingsConfigured;
          }
        }

        // additional setup to run after Bandwidth check
        const additionalSetup = (): Subscription => {
          this.selectedDialPlanGroup = this.microsoftDialPlanGroups().find(
            (msDialPlanFieldConfig: MicrosoftDialPlanFieldConfig) =>
              msDialPlanFieldConfig.id === effectiveDialPlan[0]?.id,
          );
          this.isMainNumberSelected = false;
          if (effectiveDialPlan.length === 0 || !this.selectedDialPlanGroup) {
            this.isDialPlanGroupNotFoundForExistingLine = true;
          } else {
            this.isDialPlanAccessForbidden = false;
            this.isDialPlanGroupNotFoundForExistingLine = false;
            this.isDisplaySharedCallingAlert = this.selectedDialPlanGroup.callingType === CallingType.SHARED_CALLING;
            this.initialDialPlanGroup = { ...this.selectedDialPlanGroup };
            this.isAdWriteDownConfigured = this.selectedDialPlanGroup.onPremAdWriteDownEnabled;
            const isExtensionType = this._isDialPlanExtensionType();
            this.dialPlanGroupSharedCallingPolicy = this._determineSharedCallingPolicyForDialPlan(
              this.selectedDialPlanGroup,
            );
            const tl = this.teamsLine();
            const resourceAccountMainLineOnExtensionDpg =
              !!this.isResourceAccount() &&
              isExtensionType &&
              !tl?.lineUriExtension &&
              tl?.lineUri === this.selectedDialPlanGroup.mainNumber;
            this.isMainNumberSelected = resourceAccountMainLineOnExtensionDpg;
            this.entity = {
              dialPlanGroupId: effectiveDialPlan[0].id,
              extension: isExtensionType
                ? tl.lineUriExtension || (resourceAccountMainLineOnExtensionDpg ? tl.lineUri : '')
                : this.selectedDialPlanGroup.callingType === CallingType.DID
                  ? this.teamsLine().lineUri
                  : '',
            };
            this.entitySource.next({ ...this.entity });
            this._lastSelectedExtension = this.entity.extension;

            let dialPlanInventoriesObs: Observable<void> = of(null);
            if (this.selectedDialPlanGroup.callingType !== CallingType.SHARED_CALLING) {
              dialPlanInventoriesObs = this.microsoftDialPlanInventoriesResource
                .get(this.tenantId(), effectiveDialPlan[0].id)
                .pipe(
                  map((dialPlanInventories) => {
                    this.mainNumberInventoryEntry = dialPlanInventories.mainNumber ?? null;
                    this.dialPlanInventories = this.filterOperatorConnectMsCallingPlanUsageTypes(dialPlanInventories);
                  }),
                  catchError((err) => {
                    if (err.status === 422) {
                      return EMPTY;
                    } else {
                      throw err;
                    }
                  }),
                );
            }

            let ziroDraasSharedCallingObs: Observable<void> = of(null);
            if (
              this._isZiroDraasSharedMainNumberDialPlan(this.selectedDialPlanGroup) &&
              !!this.teamsLine()?.lineUriExtension
            ) {
              ziroDraasSharedCallingObs = this.draasSharedExtensionsResource
                .getExtension(this.tenantId(), this.teamsLine().lineUri, this.teamsLine().lineUriExtension)
                .pipe(
                  map((sharedCallingExtension) => {
                    this.initialZiroDraasSharedCallingExtension = sharedCallingExtension;
                  }),
                  catchError((err) => {
                    if (err.status === 404) {
                      // in this case the original extension was not configured on the sbc. ignore this.
                      return of(null);
                    }
                    throw err;
                  }),
                );
            }

            const initialExistingNumber = [
              this.dialPlanGroupSharedCallingPolicy?.resourceAccountLineUri,
              this.initialLineUriExtension,
              this.initialLineUri,
              this.initialDialPlanGroup?.mainNumber,
            ].find((num) => !!num);

            if (!this.initialExistingNumber && initialExistingNumber) {
              this.initialExistingNumber = initialExistingNumber;
            }

            return forkJoin([dialPlanInventoriesObs, ziroDraasSharedCallingObs])
              .pipe(switchMap(() => this._applyComponentConfigWithRetry()))
              .subscribe({
                next: () => {
                  this._initFormConfig();
                  this.isSearching = false;
                  this.isDisplayForm = true;
                },
              });
          }
        };

        // Get Bandwidth field config and then search using the provided EndpointId
        if (this.isBandwidthRange) {
          this.bandwidthEmergencyCallingFieldConfigResource
            .get(this.tenantId(), this.upn(), this.teamsLine().lineUri)
            .subscribe((bandwidthEmergencyCallingFieldConfig) => {
              this.bandwidthEmergencyCallingFieldConfig = bandwidthEmergencyCallingFieldConfig;
              this.bandwidthE911DlrsResource
                .get(this.tenantId(), bandwidthEmergencyCallingFieldConfig.dynamicGeolocationEndpointId.value)
                .pipe(
                  catchError((err: HttpErrorResponse) => {
                    if (err.status === 404) {
                      const defaultEntity = {
                        callbackNumber: bandwidthEmergencyCallingFieldConfig.dynamicGeolocationCallbackNumber.value,
                        callerName: bandwidthEmergencyCallingFieldConfig.dynamicGeolocationCallerName.value,
                        geolocation: bandwidthEmergencyCallingFieldConfig.dynamicGeolocations.defaultOption,
                        preferredLanguage:
                          bandwidthEmergencyCallingFieldConfig.dynamicGeolocationPreferredLanguage.defaultOption,
                        id: bandwidthEmergencyCallingFieldConfig.dynamicGeolocationEndpointId.value,
                      };
                      this.isBandwidthConfiguredWithoutE911Dlr = true;
                      return of<BandwidthE911Dlr>(defaultEntity);
                    } else {
                      throw new Error(err.message);
                    }
                  }),
                )
                .subscribe((bandwidthE911Dlr: BandwidthE911Dlr) => {
                  // this response is passed to the child as a form entity for validation
                  this.bandwidthEntity = {
                    dynamicGeolocations: bandwidthE911Dlr.geolocation,
                    dynamicGeolocationCallerName: bandwidthE911Dlr.callerName,
                    dynamicGeolocationCallbackNumber: bandwidthE911Dlr.callbackNumber,
                    dynamicGeolocationPreferredLanguage: bandwidthE911Dlr.preferredLanguage,
                    dynamicGeolocationEndpointId: bandwidthE911Dlr.id,
                  };
                  this.initialBandwidthEndpointId = bandwidthE911Dlr.id;
                  additionalSetup();
                });
            });
        } else {
          additionalSetup();
        }
      });
    }
    this._initFormConfig();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  validateForm(): void {
    this.isSubmitted = true;
    this.msTeamsCallingFormComponent?.triggerValidation();
  }

  isCallingFormValid(): boolean {
    if (!this.selectedDialPlanGroup) {
      return false;
    }
    if (
      this.selectedDialPlanGroup.callingType !== CallingType.SHARED_CALLING &&
      !this.entity?.extension &&
      !this.canSaveWithMainNumberOnly()
    ) {
      return false;
    }
    return this.msTeamsCallingFormComponent?.areAllFormsValid() ?? true;
  }

  canSaveWithMainNumberOnly(): boolean {
    return this._isDialPlanExtensionType() && this.isResourceAccount() && this.isMainNumberSelected;
  }

  isMainNumberAssignedToOtherUser(): boolean {
    const entry = this.mainNumberInventoryEntry;
    if (!entry || entry.assignmentStatus !== DialPlanGroupEntryStatus.ASSIGNED) {
      return false;
    }
    const assignedUpn = (entry.description ?? '').trim().toLowerCase();
    const currentUpn = (this.upn() ?? '').trim().toLowerCase();
    return !!assignedUpn && assignedUpn !== currentUpn;
  }

  isMainNumberRadioDisabled(): boolean {
    return this.isMainNumberAssignedToOtherUser() && !this.isMainNumberSelected;
  }

  mainNumberAssignedDescription(): string | null {
    return this.isMainNumberAssignedToOtherUser() ? (this.mainNumberInventoryEntry?.description ?? null) : null;
  }

  selectMainNumberOnly(): void {
    if (!this._isDialPlanExtensionType() || !this.isResourceAccount()) {
      return;
    }
    if (this.isMainNumberRadioDisabled()) {
      return;
    }
    if (!this.selectedDialPlanGroup?.mainNumber) {
      return;
    }
    if (this.entity?.extension) {
      this._lastSelectedExtension = this.entity.extension;
    }
    this.isMainNumberSelected = true;
    this.entity = { ...this.entity, extension: '' };

    this.showValue = false;
    const teamsLineValue = this.teamsLine();
    if (teamsLineValue) {
      teamsLineValue.lineUriExtension = '';
      teamsLineValue.lineUri = this.selectedDialPlanGroup.mainNumber;
    }
    this.entitySource.next({ ...this.entity });
    this._syncExtensionRequiredState();
    this._loadConfigsForMainNumberAssignment();
  }

  clearMainNumberOnly(): void {
    if (!this._isDialPlanExtensionType() || !this.isResourceAccount()) {
      return;
    }
    this.isMainNumberSelected = false;
    const restore =
      this._lastSelectedExtension &&
      this.dialPlanInventories.some((entry) => entry.value === this._lastSelectedExtension)
        ? this._lastSelectedExtension
        : '';
    this.entity = { ...this.entity, extension: restore };
    this.entitySource.next({ ...this.entity });
    this._syncExtensionRequiredState();
    if (!restore) {
      this.showValue = true;
    }
  }

  toggleMainNumberDirectLine(): void {
    if (!this._isDialPlanExtensionType() || !this.isResourceAccount()) {
      return;
    }
    if (this.isMainNumberRadioDisabled()) {
      return;
    }
    if (this.isMainNumberSelected) {
      this.clearMainNumberOnly();
    } else {
      this.selectMainNumberOnly();
    }
  }

  onMainNumberDirectLineClick(event: MouseEvent): void {
    event.preventDefault();
    this.toggleMainNumberDirectLine();
  }

  onMainNumberRadioKeydown(event: KeyboardEvent): void {
    if (event.code !== 'Space' && event.code !== 'Enter') {
      return;
    }
    event.preventDefault();
    this.toggleMainNumberDirectLine();
  }

  save(): Observable<void | null> {
    if (!this.msTeamsCallingFormComponent || !this.msTeamsCallingFormComponent.areAllFormsValid()) {
      return of(null);
    }
    return this.msTeamsCallingFormComponent.performSave();
  }

  delete(): Observable<void> {
    return this.msTeamsCallingFormComponent.delete().pipe(
      tap(() => {
        this._ziroFormStateService.setIsFormDirty(false);
      }),
    );
  }

  onDialPlanGroupChange(): void {
    this.hasExtensionError = false;
    this.isMainNumberSelected = false;
    this.mainNumberInventoryEntry = null;

    if (this.selectedDialPlanGroup) {
      this.dialPlanInventories = [];
      this.isSearching = true;
      this.isBandwidthRange = this.selectedDialPlanGroup.emergencyCallingSettingsConfigured;
      this.isAdWriteDownConfigured = this.selectedDialPlanGroup.onPremAdWriteDownEnabled;

      this._setLicenseIncompatibleTooltip();
      this.dialPlanGroupSharedCallingPolicy = this._determineSharedCallingPolicyForDialPlan(this.selectedDialPlanGroup);

      if (this.selectedDialPlanGroup.callingType === CallingType.SHARED_CALLING) {
        this.isDisplaySharedCallingAlert = false;
        this.microsoftVoicePoliciesFieldConfigResource
          .get(this.tenantId(), this.selectedDialPlanGroup.id, this.isResourceAccount())
          .subscribe((voicePoliciesFieldConfig) => {
            this.voicePoliciesFieldConfig = voicePoliciesFieldConfig;
            this.isDisplaySharedCallingAlert = true;
            this.isSearching = false;
          });
      } else if (!this.licenseIncompatibleAlert) {
        const requestId: number = this.selectedDialPlanGroup.id;
        this.microsoftDialPlanInventoriesResource.get(this.tenantId(), requestId).subscribe((msDialPlanInventory) => {
          // The selected dial plan group can change again before the request completes. This can cause mismatch data if
          // the later request finishes before the original
          if (requestId !== this.selectedDialPlanGroup.id) {
            return;
          }

          if (this.entity.extension) {
            this._lastSelectedExtension = this.entity.extension;
          }
          const dialPlanInventories = msDialPlanInventory.inventory;
          const extensionOnNewDialPlan =
            this._lastSelectedExtension &&
            dialPlanInventories.some((entry) => entry.value === this._lastSelectedExtension)
              ? this._lastSelectedExtension
              : '';
          this.entitySource.next({
            ...this.entity,
            extension: extensionOnNewDialPlan,
          });

          this.mainNumberInventoryEntry = msDialPlanInventory.mainNumber ?? null;
          this.dialPlanInventories = this.filterOperatorConnectMsCallingPlanUsageTypes(msDialPlanInventory);
          this.isSearching = false;
          this.applyComponentConfig();
        });
      } else {
        this.isSearching = false;
      }
    }
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _setLicenseIncompatibleTooltip(): void {
    const ms360View = this.ms360View();
    if (
      ms360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSED &&
      this.selectedDialPlanGroup.pstnConnectivityType === PstnConnectivityType.MICROSOFT_CALLING_PLANS &&
      this.selectedDialPlanGroup.callingType !== CallingType.SHARED_CALLING &&
      !this.isResourceAccount()
    ) {
      this.licenseIncompatibleAlert = 'tkey;microsoft_360.view.teams.calling.permissions.incompatible_license';
    } else if (
      ms360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSED_WITH_MICROSOFT_CALLING_PLAN &&
      this.selectedDialPlanGroup.pstnConnectivityType !== PstnConnectivityType.MICROSOFT_CALLING_PLANS
    ) {
      this.licenseIncompatibleAlert =
        'tkey;microsoft_360.view.teams.calling.permissions.incompatible_microsoft_license';
    } else {
      this.licenseIncompatibleAlert = null;
    }
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        dialPlanGroupId: {
          label: '',
          dataAutomation: 'ms-selector-group',
          required: true,
          hidden: () => true,
        },
        extension: {
          label: 'Available Numbers',
          dataAutomation: 'ms-extension-selector',
          required: () =>
            this.selectedDialPlanGroup?.callingType !== CallingType.SHARED_CALLING && !this.canSaveWithMainNumberOnly(),
          componentConfig: new ZiroExtensionSelectorConfig({
            options: this.dialPlanInventories,
            showRecentlyDeleted: this.showRecentlyDeleted,
            showUnavailable: this.showUnavailable,
            size: ExtensionSelectorSize.LARGE,
          }),
          validation: [
            {
              validator: (val) => {
                if (!val && this.canSaveWithMainNumberOnly()) {
                  this.hasExtensionError = false;
                  return ZiroFormsValidationState.VALID;
                }
                this.isNumberLocationValidationInProgress = true;
                this.userUsageLocation = this.ms360View().microsoft365UserLicenses.usageLocation;
                this.numberUsageLocation = this.dialPlanInventories.find(
                  (dpgEntry) => dpgEntry.value === val,
                )?.usageLocation;

                this.hasExtensionError =
                  this.numberUsageLocation?.length &&
                  !(this.userUsageLocation.toLowerCase().trim() === this.numberUsageLocation.toLowerCase().trim());
                this.isNumberLocationValidationInProgress = false;
                return this.hasExtensionError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: () => {
                return {
                  content: 'tkey;microsoft_360.view.teams.extensionSelection.validation.number_usage_location',
                  params: {
                    upn: this.upn(),
                    numberLocation: this.numberUsageLocation,
                    userLocation: this.userUsageLocation,
                    dpgName: this.selectedDialPlanGroup?.name,
                  },
                };
              },
            },
            {
              validator: () => {
                return this.selectedDialPlanGroup?.callingType === CallingType.SHARED_CALLING &&
                  !this.dialPlanGroupSharedCallingPolicy
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
            },
          ],
          debounceTime: 0,
        },
      },
    } as ZiroFormConfig;
  }

  applyComponentConfig(): void {
    const extension = this.fieldComponents.find((field) => field.fieldId() === 'extension');
    extension.applyComponentConfig(
      new ZiroExtensionSelectorConfig({
        options: this.dialPlanInventories,
        showRecentlyDeleted: this.showRecentlyDeleted,
        showUnavailable: this.showUnavailable,
        size: ExtensionSelectorSize.LARGE,
      }),
    );
  }

  private _isDialPlanExtensionType(): boolean {
    return this.selectedDialPlanGroup && this.selectedDialPlanGroup.callingType === CallingType.EXTENSION;
  }

  /** Shared-calling branch emits field configs; line-uri search emits {@link MicrosoftDialPlanGroupSearchResult}s. */
  private _normalizeDialPlanLoadResultToSearchResults(
    dialPlan: MicrosoftDialPlanGroupSearchResult[] | MicrosoftDialPlanFieldConfig[],
  ): MicrosoftDialPlanGroupSearchResult[] {
    if (!dialPlan?.length) {
      return [];
    }
    const first = dialPlan[0];
    if ('url' in first) {
      return dialPlan as MicrosoftDialPlanGroupSearchResult[];
    }
    return (dialPlan as MicrosoftDialPlanFieldConfig[]).map((g) => ({ id: g.id, name: g.name, url: '' }));
  }

  /**
   * Inventory search does not return a DPG when the assigned line is the group's main number with no extension
   * (resource account main-line assignment). Resolve from field configs by main number instead.
   */
  private _resolveDialPlanSearchResultFromLineUri(
    dialPlanSearchResults: MicrosoftDialPlanGroupSearchResult[],
    teamsLineAssignment: TeamsPhoneNumberAssignment | null | undefined,
  ): MicrosoftDialPlanGroupSearchResult[] {
    if (dialPlanSearchResults?.length) {
      return dialPlanSearchResults;
    }
    if (teamsLineAssignment?.lineUri && this.isResourceAccount()) {
      const fallback = this.microsoftDialPlanGroups()?.find((g) => g.mainNumber === teamsLineAssignment.lineUri);
      if (fallback) {
        return [{ id: fallback.id, name: fallback.name, url: '' }];
      }
    }
    return dialPlanSearchResults ?? [];
  }

  private filterOperatorConnectMsCallingPlanUsageTypes(
    msDialPlanInventory: MicrosoftDialPlanInventory,
  ): DialPlanGroupEntry[] | ExtensionSelectorOption[] {
    const dialPlanInventories = msDialPlanInventory.inventory;
    if (!this.selectedDialPlanGroup) {
      return dialPlanInventories;
    }
    const isOperatorConnectOrMsCallingPlanNumbers =
      this.selectedDialPlanGroup.pstnConnectivityType === PstnConnectivityType.MICROSOFT_CALLING_PLANS ||
      this.selectedDialPlanGroup.pstnConnectivityType === PstnConnectivityType.OPERATOR_CONNECT;

    return dialPlanInventories.map((entry) => {
      if (
        !isOperatorConnectOrMsCallingPlanNumbers ||
        entry.usageTypes.includes(this.ms360View().accountType as AccountType & UsageType) ||
        entry.usageTypes.includes(UsageType.NOT_APPLICABLE)
      ) {
        return entry;
      }

      if (entry.usageTypes.includes(UsageType.OC_OR_CP_NUMBER_NOT_FOUND_IN_TEAMS)) {
        (entry as ExtensionSelectorOption).tooltip = {
          message:
            'tkey;microsoft_360.view.teams.extensionSelection.tooltip.usage_type_account_type_number_not_in_teams',
        };
        (entry as ExtensionSelectorOption).isDisabled = true;

        return entry;
      }

      if (
        !entry.usageTypes.includes(this.ms360View().accountType as AccountType & UsageType) &&
        entry.assignmentStatus !== DialPlanGroupEntryStatus.ASSIGNED
      ) {
        const capitalizedType = convertScreamingSnakeCaseToTitleCase(this.ms360View().accountType as string);
        const accountType =
          capitalizedType === 'Resource Account'
            ? capitalizedType.split(' ')[0].toLowerCase()
            : capitalizedType.toLowerCase();

        (entry as ExtensionSelectorOption).tooltip = {
          message: 'tkey;microsoft_360.view.teams.extensionSelection.tooltip.usage_account_type_mismatch',
          params: {
            usageType: capitalizedType,
            accountType,
          },
        };
        (entry as ExtensionSelectorOption).isDisabled = true;
      }

      return entry;
    });
  }

  private _isZiroDraasSharedMainNumberDialPlan(dialPlanFieldConfig: MicrosoftDialPlanFieldConfig): boolean {
    const ziroDraasPstnTypes = [PstnConnectivityType.ZIRO_DRAAS, PstnConnectivityType.ZIRO_DRAAS_BYOC];
    return (
      dialPlanFieldConfig?.callingType === CallingType.EXTENSION &&
      ziroDraasPstnTypes.includes(dialPlanFieldConfig?.pstnConnectivityType)
    );
  }

  private _handleNoPermissionForDialPlanGroup(error: any): void {
    const description = error.error.description.match(/\[(.*?)\]/);
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.FORBIDDEN,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        title: this._translateService.instant('tkey;microsoft_360.view.teams.calling.permissions.modal.title'),
        promptBody: this._translateService.instant('tkey;microsoft_360.view.teams.calling.permissions.modal.text', {
          group: description[1],
        }),
        displayCloseButton: false,
        buttons: [
          {
            label: 'tkey;dialogs.button.ok',
            buttonClass: ButtonStyles.OUTLINE_INFO,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              this.router.navigate(['..'], { relativeTo: this.route });
              return of(null);
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _determineSharedCallingPolicyForDialPlan(dialPlan: MicrosoftDialPlanFieldConfig): SharedCallingRoutingPolicy {
    if (dialPlan.callingType !== CallingType.SHARED_CALLING || !dialPlan.mainNumber) {
      return null;
    }
    return this.sharedCallingPolicies().find((policy) => policy.resourceAccountLineUri === dialPlan.mainNumber);
  }

  // This is a temporary method. Please forgive its sinful nature.
  // To be fixed in https://github.com/Stack8/smacs/issues/18109
  private _waitUntilSelectedDialPlanIsSet(): Observable<void> {
    if (
      !this.selectedDialPlanGroup &&
      !this.isDialPlanGroupNotFoundForExistingLine &&
      this.microsoftDialPlanGroups().length > 0
    ) {
      return timer(200).pipe(switchMap(() => this._waitUntilSelectedDialPlanIsSet()));
    }
    return of(null);
  }

  // Another naughty method
  private _applyComponentConfigWithRetry(): Observable<void> {
    if (!this.fieldComponents) {
      return timer(200).pipe(switchMap(() => this._applyComponentConfigWithRetry()));
    }
    this.applyComponentConfig();
    return of(null);
  }

  private _revalidateFields(): void {
    Object.values(this.fieldChannels).forEach((value) => {
      value.validateSource.next();
    });
  }

  /** Re-evaluates extension required state when main-number-only selection toggles (#23490). */
  private _syncExtensionRequiredState(): void {
    this._setFieldStates();
    this._revalidateFields();
  }

  protected _onUpdateUsageLocation(usageLocation: string): void {
    this._toastService.push(
      ToastTypes.SUCCESS,
      ZiroIcons.LOCATION,
      'tkey;shared.toast.save.success.title',
      'tkey;microsoft_360.view.teams.usage_location.toast.message',
      { upn: this.upn() },
    );

    this.ms360View().microsoft365UserLicenses.usageLocation = usageLocation;
    this._revalidateFields();
  }

  private _loadConfigsForMainNumberAssignment(): void {
    const mainNumber = this.selectedDialPlanGroup?.mainNumber;
    if (!mainNumber) {
      this.showValue = true;
      return;
    }
    const groupId = this.selectedDialPlanGroup.id;
    forkJoin([
      this.microsoftPhoneNumberAssignmentFieldConfigResource.get(this.tenantId(), mainNumber),
      this.microsoftVoicePoliciesFieldConfigResource.get(this.tenantId(), groupId, this.isResourceAccount()),
    ])
      .pipe(
        catchError((err) => {
          this.isMainNumberSelected = false;
          this.entity = { ...this.entity, extension: this._lastSelectedExtension ?? '' };
          this.entitySource.next({ ...this.entity });
          this.showValue = true;
          this._syncExtensionRequiredState();
          this._toastService.push(
            ToastTypes.ERROR,
            this.ziroIcons.EXCLAMATION,
            'tkey;shared.toast.operation_failed.title',
            err?.error?.description ??
              'tkey;microsoft_360.view.teams.extensionSelection.main_number.load_failed.message',
          );
          return EMPTY;
        }),
      )
      .subscribe(([phoneNumberAssignmentFieldConfig, voicePoliciesFieldConfig]) => {
        this.phoneNumberAssignmentFieldConfig = phoneNumberAssignmentFieldConfig;
        this.voicePoliciesFieldConfig = voicePoliciesFieldConfig;
        this.showValue = true;
        this._syncExtensionRequiredState();
      });
  }
}
